import Styles from './styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { PhotoRow, StaffOption } from '../types';
import { saveDraft, hardDelete } from '../postActions';
import BasicInfoSection from './1.basicInfo/index';
import TagSection from './2.tags/index';
import ConfirmDialog from '../confirm/index';

type PhotoEditDrawerProps = {
  row: PhotoRow;
  staffList: StaffOption[];
  time: number;
  onClose: () => void;
};

type Draft = {
  title: string;
  description: string;
  tags: string[];
  staffId: string;
};

function draftFromRow(row: PhotoRow): Draft {
  return {
    title: row.post_title,
    description: row.post_description,
    tags: row.post_tags,
    staffId: row.post_staff_id,
  };
}

const PhotoEditDrawer: React.FC<PhotoEditDrawerProps> = ({ row, staffList, time, onClose }) => {

  const [ draft, setDraft ] = useState<Draft>(() => draftFromRow(row));
  const [ newImageFile, setNewImageFile ] = useState<File | null>(null);
  const [ stagedPreviewUrl, setStagedPreviewUrl ] = useState<string | null>(null);
  const [ saving, setSaving ] = useState(false);
  const [ confirmDiscard, setConfirmDiscard ] = useState(false);
  const [ confirmDelete, setConfirmDelete ] = useState(false);

  // rowが指す作品自体が切り替わった時だけdraftを初期化しなおす。
  // (onSnapshotによる同一idのリアルタイム更新で編集中の内容が上書きされないようにする)
  useEffect(() => {
    setDraft(draftFromRow(row));
    setNewImageFile(null);
    setStagedPreviewUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  const isDirty = useMemo(() => {
    const original = draftFromRow(row);
    return (
      draft.title !== original.title ||
      draft.description !== original.description ||
      draft.staffId !== original.staffId ||
      draft.tags.length !== original.tags.length ||
      draft.tags.some((t) => !original.tags.includes(t)) ||
      newImageFile !== null
    );
  }, [draft, row, newImageFile]);

  function handleSelectImage(file: File) {
    setNewImageFile(file);
    setStagedPreviewUrl(URL.createObjectURL(file));
  }

  function handleClose() {
    if (isDirty) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveDraft(row.id, draft, newImageFile);
      onClose();
    } catch (error) {
      console.error('save failed:', error);
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    try {
      await hardDelete(row.id);
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      console.error('delete failed:', error);
      setConfirmDelete(false);
    }
  }

  const imageUrl = `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/image%2Fresize_images%2F${row.id}_500x500?alt=media&token=?${time}`;

  return (
    <>
    <div className={Styles.panel}>
      <div className={Styles.header}>
        <h4>作品を編集</h4>
        <IoClose className={Styles.closeIcon} onClick={handleClose} />
      </div>

      <div className={Styles.body}>

        <BasicInfoSection
          imageUrl={imageUrl}
          stagedPreviewUrl={stagedPreviewUrl}
          onSelectImage={handleSelectImage}
        />

        <div className={Styles.section}>
          <p className={Styles.sectionTitle}>タグ設定</p>
          <TagSection
            tags={draft.tags}
            onChange={(tags) => setDraft((d) => ({ ...d, tags }))}
          />
        </div>

      </div>

      <div className={Styles.footer}>
        <button className={Styles.deleteButton} onClick={() => setConfirmDelete(true)}>削除</button>
        <button className={Styles.saveButton} disabled={!isDirty || saving} onClick={handleSave}>
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </div>

    {confirmDiscard && (
      <ConfirmDialog
        title="変更を破棄しますか？"
        desc={"変更内容が保存されていません。\n破棄して閉じますか？"}
        confirmLabel="破棄して閉じる"
        danger
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    )}

    {confirmDelete && (
      <ConfirmDialog
        title="この作品を削除しますか？"
        desc={"この操作は取り消せません。作品データは完全に削除されます。"}
        confirmLabel="削除する"
        danger
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    )}
    </>
  );
}

export default PhotoEditDrawer;
